import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Home, BookOpen, BarChart3, Target, Bot, Upload, CreditCard,
  Mail, Settings, LogOut, Menu, X, Users, AlertTriangle,
  Stethoscope, Trophy, Flame, ChevronDown, Briefcase, FlaskConical,
  GraduationCap, ShoppingBag, ClipboardList, Package, FileText,
  Sun, Moon
} from 'lucide-react';
import Icon3D from './Icon3D';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const { theme, toggleTheme, isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if current page is Contact page for reversed header colors
  const isContactPage = location.pathname === '/contact';

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Lock horizontal scroll globally and measure scrollbar width
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    // Measure scrollbar width and set as CSS variable so header can compensate
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    return () => {
      document.body.style.overflowX = '';
      document.documentElement.style.overflowX = '';
      document.documentElement.style.removeProperty('--scrollbar-width');
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-dropdown')) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Check if user is admin
  const isAdmin = user?.is_admin === true;

  // Check if user has paid subscription
  const userTier = user?.subscription_plan || user?.subscription_tier || 'free';
  const userStatus = user?.subscription_status || 'free';
  const isPaidUser = isAdmin || (userStatus !== 'free' && userTier !== 'free' &&
      (userStatus === 'active' || userStatus === 'free_grant'));

  // Pages accessible to free users (no subscription required)
  const freeAccessPaths = ['/', '/questions', '/subscription', '/contact'];

  // Get tenant branding
  const tenantName = tenant?.name || 'MedMCQ';

  // Mock user stats (you can replace with real data)
  const userLevel = 12;
  const currentXP = 2450;
  const nextLevelXP = 3000;
  const streakDays = 14;
  const xpProgress = (currentXP / nextLevelXP) * 100;

  // Navigation items for top bar - all main pages
  const mainNavItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/questions', label: 'Questions', icon: BookOpen },
    { path: '/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/exam', label: 'Exam', icon: Target },
    { path: '/diagnostic', label: 'Diagnostic', icon: ClipboardList },
    { path: '/ai', label: 'AI', icon: Bot },
    { path: '/import', label: 'Import', icon: Upload },
    { path: '/jobs', label: 'Jobs', icon: Briefcase },
    { path: '/uniforms', label: 'Gear', icon: ShoppingBag },
    { path: '/tutoring', label: 'Tutoring', icon: GraduationCap },
    { path: '/research', label: 'Research', icon: FlaskConical },
    { path: '/subscription', label: 'Plans', icon: CreditCard },
    { path: '/contact', label: 'Contact', icon: Mail },
  ];

  // Mobile-only nav: trimmed to the 5 most important pages
  const mobileNavItems = [
    { path: '/', label: 'Home', icon: Home, colour: 'turquoise' },
    { path: '/questions', label: 'Questions', icon: BookOpen, colour: 'blue' },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, colour: 'purple' },
    { path: '/diagnostic', label: 'Diagnostic', icon: ClipboardList, colour: 'green' },
    { path: '/subscription', label: 'Plans', icon: CreditCard, colour: 'purple' },
  ];

  // Admin navigation items
  const adminNavItems = [
    { path: '/admin', label: 'Admin Dashboard', icon: Settings },
    { path: '/admin/questions', label: 'Questions', icon: FileText },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/crm', label: 'CRM', icon: Users },
    { path: '/admin/email', label: 'Email Users', icon: Mail },
    { path: '/admin/reported-issues', label: 'Reported Issues', icon: AlertTriangle },
    { path: '/admin/job-boards', label: 'Job Boards', icon: Briefcase },
    { path: '/admin/tutoring', label: 'Tutoring', icon: GraduationCap },
    { path: '/admin/gear', label: 'Gear', icon: Package },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Country flag emoji lookup
  const getCountryFlag = (country) => {
    const flags = {
      'Australia': '🇦🇺', 'New Zealand': '🇳🇿', 'United Kingdom': '🇬🇧',
      'United States': '🇺🇸', 'Canada': '🇨🇦', 'Ireland': '🇮🇪',
      'Singapore': '🇸🇬', 'Malaysia': '🇲🇾', 'India': '🇮🇳',
    };
    return flags[country] || '🌍';
  };

  // Get user initials
  const getUserInitials = () => {
    const name = user?.full_name || user?.email || '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-200 dark:bg-slate-900 transition-colors">
      {/* Top Header - Dark Navy (or Light Grey for Contact page) */}
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          height: '72px',
          backgroundColor: isContactPage ? '#E0E0E0' : '#1a2333'
        }}
      >
        <div
          className="h-full flex items-center justify-between"
          style={{
            paddingLeft: '1.5rem',
            paddingRight: 'calc(1.5rem + var(--scrollbar-width, 15px))',
            minWidth: 0
          }}
        >
          {/* Left Side - Logo */}
          <div className="flex items-center">
            {/* Logo - larger 3D stethoscope icon */}
            <Link to="/" className="flex items-center gap-2">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center relative overflow-hidden flex-shrink-0"
                style={{
                  background: 'linear-gradient(160deg, rgb(103, 232, 249) 0%, rgb(6, 182, 212) 35%, rgb(4, 116, 143) 100%)',
                  boxShadow: '0 12px 32px rgba(6,182,212,0.8), 0 6px 12px rgba(0,0,0,0.45), 0 2px 4px rgba(0,0,0,0.3), inset 0 2px 3px rgba(255,255,255,0.6), inset 0 -3px 6px rgba(0,0,0,0.35)',
                }}
              >
                {/* Specular highlight */}
                <div className="absolute" style={{ top: '3px', left: '50%', transform: 'translateX(-50%)', width: '65%', height: '40%', background: 'radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.45) 40%, transparent 100%)', borderRadius: '50%', filter: 'blur(0.5px)' }} />
                {/* Bottom darkening */}
                <div className="absolute bottom-0 left-0 right-0 h-2/5" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)' }} />
                <Stethoscope className="w-6 h-6 text-white relative z-10" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }} />
              </div>
              <span className="font-bold hidden sm:block" style={{ fontSize: '24px', color: isContactPage ? (isDark ? '#E0E0E0' : '#0a192f') : '#E0E0E0' }}>
                Med<span className="text-cyan-400">MCQ</span>
              </span>
            </Link>
          </div>

          {/* Center - Desktop Navigation (equally spaced, same size buttons) */}
          <nav className="hidden lg:flex items-center justify-center flex-1 px-4 min-w-0">
            <div className="flex items-center justify-between w-full">
              {mainNavItems.map((item) => {
                const isActive = isActivePath(item.path);
                const isLocked = !isPaidUser && !freeAccessPaths.includes(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      if (isLocked) {
                        navigate('/subscription', { state: { upgrade: true } });
                      } else {
                        handleNavClick(item.path);
                      }
                    }}
                    className={`w-20 py-1.5 rounded-lg font-medium transition-all relative overflow-hidden text-center ${
                      isActive
                        ? 'text-white'
                        : isLocked
                          ? 'opacity-40'
                          : isContactPage
                            ? 'text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-300/50 dark:hover:bg-slate-700/50'
                            : 'hover:text-white hover:bg-white/10'
                    }`}
                    style={{
                      fontSize: '14px',
                      color: isActive ? undefined : (isContactPage ? (isDark ? '#E0E0E0' : '#0a192f') : '#E0E0E0'),
                      background: isActive ? 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)' : undefined,
                      boxShadow: isActive ? '0 6px 16px rgba(6,182,212,0.55), 0 3px 6px rgba(0,0,0,0.25), inset 0 1px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)' : undefined,
                    }}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 2.5s infinite' }} />
                    )}
                    <span className="relative z-10">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Right Side - Profile Only */}
          <div className="flex items-center flex-shrink-0">
            {/* Profile Dropdown */}
            <div className="relative profile-dropdown">
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-semibold text-sm hover:opacity-90 transition-opacity relative overflow-hidden flex-shrink-0"
                style={{
                  background: 'linear-gradient(160deg, rgb(103, 232, 249) 0%, rgb(6, 182, 212) 35%, rgb(4, 116, 143) 100%)',
                  boxShadow: '0 6px 20px rgba(6,182,212,0.7), 0 3px 8px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.6), inset 0 -2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {/* Shimmer sweep */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 2.5s infinite' }} />
                {/* Specular highlight */}
                <div className="absolute" style={{ top: '2px', left: '50%', transform: 'translateX(-50%)', width: '65%', height: '40%', background: 'radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.4) 40%, transparent 100%)', borderRadius: '50%', filter: 'blur(0.5px)' }} />
                {/* Bottom darkening */}
                <div className="absolute bottom-0 left-0 right-0 h-2/5" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.08) 50%, transparent 100%)' }} />
                <span className="relative z-10 leading-none" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}>{getUserInitials()}</span>
              </button>

              {/* Dropdown Menu */}
              {profileDropdownOpen && (
                <div className="absolute right-0 top-12 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-2 z-50">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                    <p className="font-semibold text-slate-900 dark:text-white">{user?.full_name || 'User'}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                    {user?.country && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {getCountryFlag(user.country)} {user.country}
                      </p>
                    )}
                    {isAdmin && (
                      <span className="inline-block mt-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                        Admin
                      </span>
                    )}
                  </div>

                  {/* Admin Section */}
                  {isAdmin && (
                    <>
                      <div className="px-4 py-1 mt-2">
                        <span className="text-xs font-semibold text-red-500 uppercase">Admin</span>
                      </div>
                      {adminNavItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.path}
                            onClick={() => {
                              setProfileDropdownOpen(false);
                              navigate(item.path);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
                          >
                            <Icon className="w-4 h-4 text-slate-400" />
                            {item.label}
                          </button>
                        );
                      })}
                    </>
                  )}

                  {/* Theme Toggle */}
                  <div className="border-t border-slate-100 dark:border-slate-700 mt-2 pt-2">
                    <button
                      onClick={toggleTheme}
                      className="w-full flex items-center gap-3 px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
                    >
                      {isDark ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-indigo-500" />}
                      {isDark ? 'Light Mode' : 'Dark Mode'}
                    </button>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-slate-100 dark:border-slate-700 mt-2 pt-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
              style={{ color: isContactPage ? (isDark ? '#E0E0E0' : '#0a192f') : '#E0E0E0' }}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            className="fixed left-0 right-0 z-40 lg:hidden"
            style={{
              top: '72px',
              backgroundColor: isContactPage ? '#E0E0E0' : '#1a2333',
              borderTop: isContactPage ? '1px solid #94A3B8' : '1px solid #1e293b'
            }}
          >
            <nav className="p-4 space-y-1">
              {mobileNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(item.path);
                const isLocked = !isPaidUser && !freeAccessPaths.includes(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      if (isLocked) {
                        handleNavClick('/subscription');
                      } else {
                        handleNavClick(item.path);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all relative overflow-hidden ${
                      isActive
                        ? 'text-white'
                        : isLocked
                          ? 'opacity-40'
                          : isContactPage
                            ? 'text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-300/50 dark:hover:bg-slate-700/50'
                            : 'text-slate-300 hover:text-white hover:bg-white/10'
                    }`}
                    style={{
                      fontSize: '18px',
                      background: isActive ? 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)' : undefined,
                      boxShadow: isActive ? '0 4px 12px rgba(6,182,212,0.5), 0 2px 6px rgba(0,0,0,0.25), inset 0 1px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)' : undefined,
                    }}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 2.5s infinite' }} />
                    )}
                    <div className="relative z-10"><Icon3D icon={Icon} colour={item.colour} size="sm" /></div>
                    <span className="relative z-10">{item.label}</span>
                  </button>
                );
              })}

              {isAdmin && (
                <div className="my-3 pt-3" style={{ borderTop: isContactPage ? '1px solid #94A3B8' : '1px solid #1e293b' }}>
                  <p className="px-4 py-2 text-xs font-semibold text-red-400 uppercase">Admin</p>
                  {adminNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavClick(item.path)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium ${
                          isContactPage
                            ? 'text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-300/50 dark:hover:bg-slate-700/50'
                            : 'text-slate-300 hover:text-white hover:bg-white/10'
                        }`}
                        style={{ fontSize: '20px' }}
                      >
                        <Icon3D icon={Icon} colour="orange" size="sm" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </nav>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="min-h-screen" style={{ paddingTop: '72px' }}>
        {children}
      </main>

      {/* Footer - Dark Blue */}
      <footer className="text-white py-6" style={{ backgroundColor: '#1a2333' }}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm">
            <span className="text-slate-400">Med</span>
            <span className="text-cyan-400">MCQ</span>
            <span className="text-slate-300"> &copy; 2025 - Medical Student Learning Platform</span>
          </p>
          <p className="text-xs text-slate-400 mt-1">
            For educational purposes only. Not for actual medical diagnosis.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
